// src/services/authService.js
import axios from 'axios';
import { buildApiUrl } from '../config/api';

const getAuthHeaders = (token) => ({
    Authorization: `Bearer ${token}`,
    'x-auth-token': token
});

const authService = {
    // Login user
    login: async (credentials) => {
        try {
            const response = await axios.post(buildApiUrl('/auth/login'), credentials);
            if (response.data.token) {
                localStorage.setItem('token', response.data.token);
                localStorage.setItem('user', JSON.stringify(response.data.user));
                window.dispatchEvent(new Event('authChange'));
                return {
                    success: true,
                    ...response.data
                };
            }

            return {
                success: false,
                message: response.data.message || 'Login failed'
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Login failed'
            };
        }
    },

    // Register user
    register: async (userData) => {
        try {
            const payload = {
                ...userData,
                username: userData.username || userData.name
            };
            const response = await axios.post(buildApiUrl('/auth/register'), payload);
            if (response.data.token) {
                localStorage.setItem('token', response.data.token);
                localStorage.setItem('user', JSON.stringify(response.data.user));
                window.dispatchEvent(new Event('authChange'));
                return {
                    success: true,
                    ...response.data
                };
            }

            return {
                success: false,
                message: response.data.message || 'Registration failed'
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Registration failed'
            };
        }
    },

    // Logout user
    logout: () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.dispatchEvent(new Event('authChange'));
    },

    // Get current user from localStorage
    getCurrentUser: () => {
        const user = localStorage.getItem('user');
        return user ? JSON.parse(user) : null;
    },

    // Get token
    getToken: () => {
        return localStorage.getItem('token');
    },

    // Check if logged in
    isLoggedIn: () => {
        return !!localStorage.getItem('token');
    },

    // Get user profile from API
    getProfile: async () => {
        try {
            const token = authService.getToken();
            const response = await axios.get(buildApiUrl('/auth/me'), {
                headers: getAuthHeaders(token)
            });
            return {
                success: true,
                user: response.data
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch profile'
            };
        }
    },

    // Update profile
    updateProfile: async (profileData) => {
        try {
            const token = authService.getToken();
            const response = await axios.put(buildApiUrl('/auth/profile'), profileData, {
                headers: getAuthHeaders(token)
            });
            if (response.data.success) {
                localStorage.setItem('user', JSON.stringify(response.data.data));
            }
            return response.data;
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to update profile'
            };
        }
    },

    // Change password
    changePassword: async (passwordData) => {
        try {
            const token = authService.getToken();
            const response = await axios.post(buildApiUrl('/auth/change-password'), passwordData, {
                headers: getAuthHeaders(token)
            });
            return {
                success: true,
                ...response.data
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to change password'
            };
        }
    },

    // Verify token
    verifyToken: async () => {
        try {
            const token = authService.getToken();
            const response = await axios.post(buildApiUrl('/auth/verify-token'), {}, {
                headers: getAuthHeaders(token)
            });
            if (response.data.success && response.data.user) {
                localStorage.setItem('user', JSON.stringify(response.data.user));
            }
            return response.data;
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Invalid token'
            };
        }
    },

    forgotPassword: async (email) => {
        try {
            const response = await axios.post(buildApiUrl('/auth/forgot-password'), { email });
            return {
                success: true,
                ...response.data
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to send reset email'
            };
        }
    },

    resetPassword: async (token, password) => {
        try {
            const response = await axios.post(buildApiUrl('/auth/reset-password'), { token, password });
            return {
                success: true,
                ...response.data
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to reset password'
            };
        }
    }
};

export default authService;
